import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { ArrowLeft, Plus, RefreshCcw, X, Save } from "lucide-react";
import AttachmentsPanel from "../../Product/Trade/AttachmentsPanel";
import "../../Product/customers.css";

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}
const money = (n) => `₦${Number(n || 0).toLocaleString()}`;

function normalizePayType(p) {
  // Backward/forward compat for your mixed schema (type + payment_type)
  return (p?.type || p?.payment_type || "").toUpperCase();
}

export default function CustomerPayments() {
  const { companyId } = useCompany();
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentsByInvoice, setPaymentsByInvoice] = useState({});

  // modal
  const [open, setOpen] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [payForm, setPayForm] = useState({
    payment_date: isoToday(),
    type: "PAYMENT",
    amount: "",
    notes: "",
  });

  async function fetchAll() {
    setErr("");
    if (!companyId || !customerId) return;
    setBusy(true);
    try {
      const { data: cust, error: cErr } = await supabase
        .from("customers")
        .select("id,name,company_name,phone,currency,open_balance")
        .eq("company_id", companyId)
        .eq("id", customerId)
        .single();
      if (cErr) throw cErr;
      setCustomer(cust);

      const { data: inv, error: iErr } = await supabase
        .from("invoices")
        .select('id,invoiceNumber,date,"expiryDate",status,total,created_at')
        .eq("company_id", companyId)
        .eq("customer_id", customerId)
        .order("date", { ascending: false })
        .limit(300);
      if (iErr) throw iErr;
      setInvoices(inv || []);

      const invIds = (inv || []).map((x) => x.id);
      if (!invIds.length) {
        setPaymentsByInvoice({});
        return;
      }

      // IMPORTANT: select BOTH type and payment_type for compat
      const { data: pays, error: pErr } = await supabase
        .from("invoice_payments")
        .select(
          "id,invoice_id,payment_date,type,payment_type,amount,notes,created_at"
        )
        .eq("company_id", companyId)
        .in("invoice_id", invIds)
        .order("payment_date", { ascending: false });
      if (pErr) throw pErr;

      const grouped = {};
      (pays || []).forEach((p) => {
        grouped[p.invoice_id] = grouped[p.invoice_id] || [];
        grouped[p.invoice_id].push(p);
      });
      setPaymentsByInvoice(grouped);
    } catch (e) {
      setErr(e?.message || "Failed to load customer payments.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, customerId]);

  const computed = useMemo(() => {
    const rows = invoices.map((inv) => {
      const pays = paymentsByInvoice[inv.id] || [];
      const paid = pays
        .filter((p) => normalizePayType(p) === "PAYMENT")
        .reduce((s, p) => s + Number(p.amount || 0), 0);
      const refunded = pays
        .filter((p) => normalizePayType(p) === "REFUND")
        .reduce((s, p) => s + Number(p.amount || 0), 0);

      // balance = total - netPaid + refunds (refund increases what is owed again)
      const balance = Number(inv.total || 0) - paid + refunded;

      return { ...inv, paid, refunded, balance, pays };
    });

    return {
      rows,
      totalInvoiced: rows.reduce((s, r) => s + Number(r.total || 0), 0),
      totalPaidNet: rows.reduce((s, r) => s + (r.paid - r.refunded), 0),
      totalOutstanding: rows.reduce(
        (s, r) => s + Math.max(0, Number(r.balance || 0)),
        0
      ),
    };
  }, [invoices, paymentsByInvoice]);

  function openPaymentModal(inv) {
    setActiveInvoice(inv);
    setPayForm({
      payment_date: isoToday(),
      type: "PAYMENT",
      amount: "",
      notes: "",
    });
    setOpen(true);
  }

  async function submitPayment() {
    setErr("");
    if (!companyId) return setErr("Missing company.");
    if (!activeInvoice?.id) return setErr("Select an invoice.");
    const amount = Number(payForm.amount || 0);
    if (!(amount > 0)) return setErr("Enter a valid amount (> 0).");

    setBusy(true);
    try {
      // Keep existing RPC (don’t remove features). But send the correct "type" name.
      const { error } = await supabase.rpc("record_invoice_payment", {
        p_company_id: companyId,
        p_invoice_id: activeInvoice.id,
        p_payment_date: payForm.payment_date,
        p_payment_type: payForm.type, // backend expects p_payment_type
        p_amount: amount,
        p_notes: payForm.notes?.trim() || null,
        p_bank_account_id: null,
      });
      if (error) throw error;

      setOpen(false);
      setActiveInvoice(null);
      await fetchAll();
    } catch (e) {
      setErr(e?.message || "Failed to record payment/refund.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="customers-container">
      <header className="cust-head panel" style={{ display: "flex", gap: 10 }}>
        <button className="btn" onClick={() => navigate(-1)} disabled={busy}>
          <ArrowLeft size={16} /> Back
        </button>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: 16 }}>Customer Payments</div>
          <div className="muted" style={{ fontSize: 13 }}>
            Payments/refunds should be reflected in reports via journals
            (ledger-first).
          </div>
        </div>

        <button className="btn-ghost" onClick={fetchAll} disabled={busy}>
          <RefreshCcw size={16} /> Refresh
        </button>
      </header>

      {err && <div className="banner error">{err}</div>}

      <section className="panel" style={{ padding: 14 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={{ minWidth: 260 }}>
            <div style={{ fontWeight: 900 }}>
              {customer?.name || "—"}{" "}
              <span className="muted">
                {customer?.company_name ? `• ${customer.company_name}` : ""}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {customer?.phone || ""}
            </div>
          </div>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div className="chip">
              Total Invoiced: <b>{money(computed.totalInvoiced)}</b>
            </div>
            <div className="chip">
              Paid (net): <b>{money(computed.totalPaidNet)}</b>
            </div>
            <div className="chip">
              Outstanding: <b>{money(computed.totalOutstanding)}</b>
            </div>
          </div>
        </div>
      </section>

      <section className="panel table-panel">
        <div className="table-wrap">
          <table className="customer-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Due</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th className="num">Refunded</th>
                <th className="num">Balance</th>
                <th className="center">Action</th>
              </tr>
            </thead>
            <tbody>
              {computed.rows.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <div className="cell-primary">
                      {inv.invoiceNumber || "—"}
                    </div>
                  </td>
                  <td>{inv.date || "—"}</td>
                  <td>{inv.expiryDate || "—"}</td>
                  <td>
                    <span className="chip">
                      {String(inv.status || "open").toUpperCase()}
                    </span>
                  </td>
                  <td className="num">{money(inv.total)}</td>
                  <td className="num">{money(inv.paid)}</td>
                  <td className="num">{money(inv.refunded)}</td>
                  <td className="num">{money(inv.balance)}</td>
                  <td className="center">
                    <button
                      className="btn"
                      onClick={() => openPaymentModal(inv)}
                      disabled={busy}
                    >
                      <Plus size={16} /> Record
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!computed.rows.length && (
            <div className="empty-state">
              <p>No invoices found for this customer.</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      {open && (
        <div className="modal-overlay" onClick={() => !busy && setOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>
                Record {payForm.type === "REFUND" ? "Refund" : "Payment"}
              </h2>
              <button
                className="icon-btn"
                onClick={() => !busy && setOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              Invoice: <b>{activeInvoice?.invoiceNumber || "—"}</b> • Total{" "}
              <b>{money(activeInvoice?.total)}</b>
            </div>

            <div className="modal-form" style={{ marginTop: 10 }}>
              <div className="row" style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div
                    className="muted"
                    style={{ fontSize: 12, marginBottom: 6 }}
                  >
                    Date
                  </div>
                  <input
                    className="input"
                    type="date"
                    value={payForm.payment_date}
                    onChange={(e) =>
                      setPayForm((p) => ({
                        ...p,
                        payment_date: e.target.value,
                      }))
                    }
                    disabled={busy}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    className="muted"
                    style={{ fontSize: 12, marginBottom: 6 }}
                  >
                    Type
                  </div>
                  <select
                    className="input"
                    value={payForm.type}
                    onChange={(e) =>
                      setPayForm((p) => ({ ...p, type: e.target.value }))
                    }
                    disabled={busy}
                  >
                    <option value="PAYMENT">Payment</option>
                    <option value="REFUND">Refund</option>
                  </select>
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <div
                  className="muted"
                  style={{ fontSize: 12, marginBottom: 6 }}
                >
                  Amount
                </div>
                <input
                  className="input"
                  type="number"
                  inputMode="decimal"
                  value={payForm.amount}
                  onChange={(e) =>
                    setPayForm((p) => ({ ...p, amount: e.target.value }))
                  }
                  disabled={busy}
                />
              </div>

              <div style={{ marginTop: 10 }}>
                <div
                  className="muted"
                  style={{ fontSize: 12, marginBottom: 6 }}
                >
                  Notes (optional)
                </div>
                <textarea
                  className="input"
                  rows={3}
                  value={payForm.notes}
                  onChange={(e) =>
                    setPayForm((p) => ({ ...p, notes: e.target.value }))
                  }
                  disabled={busy}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn"
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={submitPayment}
                disabled={busy}
              >
                <Save size={16} /> {busy ? "Saving…" : "Save"}
              </button>
            </div>

            {activeInvoice?.id && (
              <AttachmentsPanel
                entityType="invoice"
                entityId={activeInvoice.id}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
